import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { showpublicFieldName } from "../../../../utils/API";
import { standardizeColor } from "../../../../utils/helper";
import CustomSwitch from "../../../CustomSwitch/CustomSwitch";
import { CiLock, InformationIcon } from "../../../Icons";
import { WarningMessage } from "../../../Report/new/Input/Input-styled";
import { Tooltip } from "../../../UI";
import {
  DescriptionWrapper,
  FieldLabel,
  SelectionBox,
  SelectionBoxDisabled,
  SelectionBoxLabel,
  SelectionTagItem,
} from "./EditorFields-styled";

export const SelectionTags = ({
  id,
  options,
  colors,
  label,
  value,
  field,
  information,
  onSelect,
  isRequired,
  borderColor,
  isDisabled,
  privateFieldText,
}) => {
  const [selectedCode, setSelectedCode] = useState(value);
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation("common");

  useEffect(() => {
    const fieldValue = getValues(field.name);

    if (!fieldValue) {
      setSelectedCode(value);
      setValue(field.name, value, { shouldValidate: false });
    } else {
      //some values are numbers and some are strings
      const convertedValue = field.type.includes("integer")
        ? Number(fieldValue)
        : fieldValue;
      const val = !Number.isNaN(convertedValue) ? convertedValue : fieldValue;
      setSelectedCode(val);
    }
  }, [value]);

  const handleSelect = useCallback(
    (option) => {
      setSelectedCode(option.code);
      setValue(field.name, option.code, {
        shouldDirty: true,
      });
      if (onSelect) {
        onSelect(option);
      }
    },
    [onSelect]
  );

  const filledInColor = useMemo(
    () => borderColor, //isRequired ? '#16a34a'  : borderColor
    [isRequired]
  );

  const errorMessage =
    errors[field.name]?.type === "required"
      ? t("screen.widget.common.fieldIsRequired")
      : "";

  return (
    <SelectionBox
      borderColor={filledInColor}
      noMargin={!errorMessage}
      isDisabled={isDisabled}
      isError={!!errorMessage}
    >
      {isDisabled && <SelectionBoxDisabled />}
      {label && (
        <SelectionBoxLabel>
          {privateFieldText && (
            <Tooltip
              wrapperStyles={{ pointerEvents: isDisabled ? "none" : "" }}
              showOnHover
              content={privateFieldText}
              placement="top"
            >
              <CiLock width="11px" height="11px" style={{ marginRight: 2 }} />
            </Tooltip>
          )}
          {label} {isRequired ? <span> *</span> : ""}
        </SelectionBoxLabel>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 6,
        }}
      >
        <select
          {...register(field.name, { required: isRequired })}
          readOnly
          id={id}
          data-field-name={field.name}
          style={{ display: "none" }}
          value={selectedCode}
          data-alias={field.alias}
        >
          {field.nullable && <option key={null} value={""}></option>}
          {options.map((cv) => (
            <option key={cv.code} value={cv.code}>
              {cv.name}
            </option>
          ))}
        </select>
        {options.map((option) => (
          <SelectionTagItem
            selected={selectedCode === option.code}
            key={option.code}
            color={standardizeColor(colors[option.code] ?? "#808080")}
            onClick={() => handleSelect(option)}
            style={{
              padding: 0,
            }}
          >
            <p>{option.name}</p>
          </SelectionTagItem>
        ))}
        {information && (
          <DescriptionWrapper>
            <Tooltip showOnClick content={information}>
              <InformationIcon width="18" height="18" />
            </Tooltip>
          </DescriptionWrapper>
        )}
      </div>
      <WarningMessage isError={!!errorMessage}>{errorMessage}</WarningMessage>
    </SelectionBox>
    // </Tooltip>
  );
};

export const EditorSwitch = ({
  id,
  label,
  value,
  field,
  information,
  onChange,
  isRequired,
  disabled,
  privateFieldText,
  editableLayer,
}) => {
  const { t } = useTranslation("common");
  // const [selected, setSelected] = useState(value || 2);
  const { register, setValue, getValues, watch } = useFormContext();
  watch(field.name);

  // useEffect(() => {
  //   console.log(formValue, value, field.name);

  //   return () => {};
  // }, [formValue]);

  useEffect(() => {
    const fieldValue = getValues(field.name);
    if (!fieldValue) {
      // setSelected(value || 2);
      // setValue(field.name, value || 2);
    } else {
      const val = field.type.includes("integer")
        ? Number(fieldValue)
        : fieldValue;
      // setSelected(val);
    }
  }, [value]);

  const onToggle = useCallback((e) => {
    const value = e ? 1 : 2;
    setValue(field.name, value, {
      shouldDirty: true,
    });
    if (onChange) {
      onChange({ name: field.name, value });
    }
    // setSelected(e ? 1 : 2);
  }, []);

  const formValue = getValues(field.name) || value || 2;
  const selected = field.type.includes("integer")
    ? Number(formValue)
    : formValue;

  const showCurrValidationTooltip = useMemo(() => {
    try {
      const show =
        disabled &&
        field.name === showpublicFieldName &&
        editableLayer.layerConfig.isShownInReportManager;
      return show;
    } catch (error) {
      return false;
    }
  }, [field, disabled, editableLayer]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",
        padding: "0px 1px",
      }}
    >
      <select
        {...register(field.name, { required: isRequired })}
        readOnly
        id={id}
        style={{ display: "none" }}
        data-field-name={field.name}
        className="esri-input esri-feature-form__input"
        value={selected}
        data-alias={field.alias}
        disabled={disabled}
      >
        {field.nullable && <option key={null} value={""}></option>}
        <option key={1} value={1}>
          {1}
        </option>
        <option key={2} value={2}>
          {2}
        </option>
      </select>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <FieldLabel style={{ fontSize: 14 }} noMargin>
          <Tooltip showOnHover content={privateFieldText} placement="top">
            <CiLock width="12px" height="12px" style={{ marginRight: 2 }} />
          </Tooltip>
          {label}{" "}
        </FieldLabel>
        {information && (
          <Tooltip showOnClick content={information}>
            <InformationIcon width="18" height="18" />
          </Tooltip>
        )}
      </div>
      <div style={{ position: "relative" }}>
        {showCurrValidationTooltip && (
          <Tooltip
            placement="top"
            content={t(
              "screen.widget.Editor.manager.reports.showPublicDisabled"
            )}
            showOnHover
            absolute
            wrapperStyles={{
              zIndex: 2,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            blurred
          />
        )}
        <CustomSwitch
          onChange={onToggle}
          checked={selected === 1}
          height={18}
          width={38}
          handleDiameter={14}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export const CustomSelect = ({
  id,
  options,
  value,
  label,
  isRequired,
  borderColor,
  information,
  onSelect,
  field,
  onInputBlur,
  privateFieldText,
  ...props
}) => {
  const [selectedCode, setSelectedCode] = useState(value);
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation("common");

  useEffect(() => {
    const fieldValue = getValues(field.name);

    if (!fieldValue) {
      setSelectedCode(value);
      setValue(field.name, value, { shouldValidate: false });
    } else {
      //some values are numbers and some are strings
      const convertedValue = field.type.includes("integer")
        ? Number(fieldValue)
        : fieldValue;
      const val = !Number.isNaN(convertedValue) ? convertedValue : fieldValue;
      setSelectedCode(val);
    }
  }, [value]);

  const handleSelect = useCallback(
    (option) => {
      setSelectedCode(option.code);
      setValue(field.name, option.code, {
        shouldDirty: true,
      });
      if (onSelect) {
        onSelect(option);
      }
    },
    [onSelect]
  );

  const filledInColor = useMemo(
    () => borderColor, //isRequired ? '#16a34a'  : borderColor
    [isRequired]
  );

  const errorMessage =
    errors[field.name]?.type === "required"
      ? t("screen.widget.common.fieldIsRequired")
      : "";

  const opsColor = borderColor || "#eee";
  const selectStyles = () => ({
    control: (baseStyles, state) => {
      return {
        ...baseStyles,
        margin: "0px 0px",
        color: "#000",
        borderColor: "#4c4c4c4d",
        boxShadow: "none",
        // boxShadow:
        //   state.isFocused && opsColor
        //     ? `0 0 0 1px ${opsColor}`
        //     : baseStyles.boxShadow,
        "&:hover": {
          borderColor: "none",
        },
        fontSize: 14,
        minHeight: 20,
        borderRadius: "8px",
      };
    },
    option: (baseStyles, state) => ({
      ...baseStyles,
      fontSize: 12,
      backgroundColor: state.isSelected ? "#eee" : "#fff",
      color: state.isSelected && opsColor ? "#000" : baseStyles.color,
      "&:hover": {
        backgroundColor: "#eee",
      },
    }),
    input: (baseStyles, state) => {
      return {
        ...baseStyles,
        height: 20,
      };
    },
    indicatorsContainer: (baseStyles) => ({
      ...baseStyles,
      alignItems: "flex-start",
      padding: 4,
    }),
    indicatorContainer: (baseStyles) => ({
      ...baseStyles,
      padding: 4,
    }),
    MultiValueRemove: (baseStyles) => ({
      ...baseStyles,
      "&:hover": {
        backgroundColor: "transparent",
      },
    }),
    menuPortal: (base) => {
      const { zIndex, ...rest } = base;
      return { ...rest, zIndex: 9999 };
    },
    menuList: (base) => ({
      ...base,
      maxHeight: 200,
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      lineHeight: "normal",
    }),
  });

  const selectedOption = options.find((option) => option.code === selectedCode);

  const onBlur = (e) => {
    onInputBlur(e, field.name);
  };

  return (
    <div
      style={{
        position: "relative",
        margin: "10px 0px 0px 0px",
        marginBottom: !!errorMessage ? 10 : 0,
      }}
    >
      {label && (
        <SelectionBoxLabel>
          <Tooltip showOnHover content={privateFieldText} placement="top">
            <CiLock width="11px" height="11px" style={{ marginRight: 2 }} />
          </Tooltip>
          {label} {isRequired ? <span> *</span> : ""}
        </SelectionBoxLabel>
      )}
      {information && (
        <DescriptionWrapper>
          <Tooltip showOnClick content={information}>
            <InformationIcon width="18" height="18" />
          </Tooltip>
        </DescriptionWrapper>
      )}
      {/* Hidden native select for form compatibility */}
      <select
        {...register(field.name, { required: isRequired })}
        readOnly
        id={id}
        data-field-name={field.name}
        style={{ display: "none" }}
        value={selectedCode}
        data-alias={field.alias}
      >
        {field.nullable && <option key={null} value={""}></option>}
        {options.map((cv) => (
          <option key={cv.code} value={cv.code}>
            {cv.name}
          </option>
        ))}
      </select>
      {/* React-select component */}
      <Select
        styles={selectStyles()}
        isSearchable={false}
        classNamePrefix={"react-select"}
        value={selectedOption}
        options={options}
        onChange={handleSelect}
        getOptionLabel={(opt) => opt.name}
        getOptionValue={(opt) => opt.code}
        onBlur={onBlur}
        {...props}
      />
      {/* Show error if required */}
      {errorMessage && <WarningMessage isError>{errorMessage}</WarningMessage>}
    </div>
  );
};
